import React, { useEffect } from 'react'
import usePaymentPage from 'src/lib/contexts/PaymentPageContext'
import { PaymentModeUiConfig } from 'src/lib/types/paymentPage'
import PaymentMethodIcon from './icons/PaymentMethodIcon'
import { CaretRight, SealPercent } from '@phosphor-icons/react'
import {
  DeviceType,
  Icon,
  UpiIntentItems,
  UPI_INTENT_APPS as apps,
  getTextColorByBrightness,
  MethodLogo,
  AppsGrid,
  hexToRgba
} from '@shopflo/ui'
import { classNames } from 'src/lib/utils/helpers'
import LogoComponent from './configs/LogoComponent'
import { useScrollableRef } from './hooks/useScrollableRef'
import { UIPreviewPaymentMethodType } from 'src/lib/types/uiPreview'
import { getActiveApps } from 'src/lib/utils/paymentSettings'
import { useLocale } from 'src/lib/hooks/useLocal'
import { IMAGE_CDN_URL } from 'src/lib/utils/constants'

interface PaymentMethodsPreviewProps {
  paymentMethodsConfig?: UIPreviewPaymentMethodType[]
  isPaymentOfferPreview?: boolean
}

type ParsedColors = {
  paymentCard: {
    borderColor: string
    backgroundColor: string
    textColor: string
    iconColor: string
    subtitleColor: string
  }
  offerBadge: {
    backgroundColor: string
    textColor: string
  }
}

const PaymentMethodsPreview: React.FC<PaymentMethodsPreviewProps> = ({
  paymentMethodsConfig,
  isPaymentOfferPreview
}) => {
  const {
    getters: { paymentTileConfigs, selection, hoveredMethodId },
    actions: { setSelection }
  } = usePaymentPage()

  const { setMethodRef, scrollToMethod } = useScrollableRef()

  const shouldScrollToMethod = (methodId: string) => {
    const method = paymentTileConfigs.modeDetails.find(m => m.mode === methodId)
    return method && method.weight > 0
  }

  useEffect(() => {
    const targetMethodId = selection.type === 'METHOD' && selection.id ? selection.id : hoveredMethodId

    if (targetMethodId && shouldScrollToMethod(targetMethodId)) {
      scrollToMethod(targetMethodId)
    }
  }, [selection.type, selection.id, hoveredMethodId, paymentTileConfigs.modeDetails, scrollToMethod])

  const visibleSortedModes = paymentTileConfigs.modeDetails
    .filter(mode => mode.weight > 0)
    .sort((a, b) => {
      const aIsUpiIntentMode = a.mode === 'UPI_INTENT'
      const bIsUpiIntentMode = b.mode === 'UPI_INTENT'

      if (aIsUpiIntentMode && !bIsUpiIntentMode) return -1
      if (bIsUpiIntentMode && !aIsUpiIntentMode) return 1

      if (a.isSuggested && !b.isSuggested) return -1
      if (!a.isSuggested && b.isSuggested) return 1

      return a.weight - b.weight
    })

  return (
    <div className='px-4 flex flex-col ' style={{ gap: paymentTileConfigs.layoutUi.paymentTileGap }}>
      {visibleSortedModes.map(mode => (
        <div key={mode.mode} ref={setMethodRef(mode.mode)} className='flex flex-col gap-2'>
          <MethodContainer
            mode={mode}
            isSelected={selection.type === 'METHOD' && selection.id === mode.mode}
            isHovered={hoveredMethodId === mode.mode}
            onSelect={() => {
              if (isPaymentOfferPreview) {
                return
              }
              setSelection({ type: 'METHOD', id: mode.mode })
            }}
            hasAnySelected={selection.type !== null && selection.id !== null}
            bannerSelected={selection.type === 'BANNER' || selection.type === 'COUPON'}
            paymentMethodsConfig={paymentMethodsConfig}
          />
        </div>
      ))}
    </div>
  )
}

export const SuggestedMethodWrapper = ({
  title,
  backgroundColor,
  textColor,
  isDimmed,
  children
}: {
  title: string
  backgroundColor: string
  textColor: string
  isDimmed: boolean
  children: React.ReactNode
}) => (
  <div
    className={classNames('rounded-[20px] px-1 pb-1 ', isDimmed ? 'opacity-50' : '')}
    style={{ backgroundColor, color: textColor }}
  >
    <p className='text-text-xs-semibold text-center py-1'>{title} </p>
    {children}
  </div>
)

const MobileUpiPreview = ({
  selectedApps,
  otherApps,
  textColor
}: {
  selectedApps: UpiIntentItems[]
  otherApps: UpiIntentItems[]
  textColor: string
}) => {
  const { t } = useLocale()
  const activeOtherApps = otherApps.filter(item => item.is_active)

  return (
    <div className='flex w-full'>
      <ul className='grid w-full auto-cols-fr items-center grid-flow-col p-2 pt-4' style={{ color: textColor }}>
        {selectedApps
          .filter(item => item.is_active)
          .map(item => (
            <li key={item.id} className='flex items-center justify-center py-1.5'>
              <MethodLogo
                id={item.id}
                logo={item.url}
                name={item.name}
                offerText={item.offer_text}
                iconClassname='h-8 w-8'
              />
            </li>
          ))}
        {activeOtherApps.length > 0 && (
          <li>
            <div className='flex cursor-pointer gap-1.5 flex-col items-center space-y-4'>
              <AppsGrid items={activeOtherApps.map(item => item.url)} className='w-8 h-8' />
              <p className='text-xxs font-medium capitalize !mt-0 opacity-[70%]'>{t('payments_tab.upi_others')}</p>
            </div>
          </li>
        )}
      </ul>
    </div>
  )
}

const DesktopUpiPreview = ({
  selectedApps,
  borderColor,
  textColor
}: {
  selectedApps: UpiIntentItems[]
  borderColor: string
  textColor: string
}) => (
  <div className='flex w-full gap-4 py-2 px-3'>
    <div className='flex items-center justify-center w-1/2'>
      <div className='h-[140px] w-[140px] border p-2 rounded-md' style={{ borderColor }}>
        <img src={`${IMAGE_CDN_URL}/qrCode.png`} width={122} height={122} alt='upi-qr' />
      </div>
    </div>
    <div className='grid grid-rows-3 py-2 items-center justify-between'>
      <p className='text-[10px] opacity-[0.6]'>Scan the QR & Pay with your UPI app of choice</p>
      <ul className='flex flex-1 h-fit'>
        {selectedApps
          .filter(item => item.is_active)
          .map((icon, index) => (
            <li
              key={index}
              className='flex items-center justify-center rounded-full h-fit ml-[-8px] first:ml-0 border border-gray-200 border-1'
            >
              <img src={icon.url} alt={icon?.name} className='w-7 h-7 rounded-full bg-white' />
            </li>
          ))}
      </ul>
      <div className='flex items-center text-[10px] gap-1'>
        <span>QR is valid for</span>
        <span
          className='font-small rounded-md text-center p-1'
          style={{ backgroundColor: hexToRgba(textColor, 0.1), color: textColor }}
        >
          4:05 min
        </span>
      </div>
    </div>
  </div>
)

const UpiIntentTileContent = ({
  mode,
  parsedColors,
  isMobile,
  isLogoContainer,
  paymentMethodsConfig,
  hideOfferBadge
}: {
  mode: PaymentModeUiConfig
  parsedColors: ParsedColors
  isMobile: boolean
  isLogoContainer: boolean
  paymentMethodsConfig?: UIPreviewPaymentMethodType[]
  hideOfferBadge?: boolean
}) => {
  const fallbackUpiApps = apps.selected_apps
  return (
    <div
      className='relative rounded-2xl bg-white w-full divide-y divide-[var(--divider-color)]'
      style={{
        color: parsedColors.paymentCard.textColor,
        backgroundColor: parsedColors.paymentCard.backgroundColor,
        ['--divider-color' as any]: getTextColorByBrightness({
          bgHexColor: parsedColors.paymentCard.backgroundColor ?? '#FFFFFF',
          lightOpacity: 10,
          darkOpacity: 6
        })
      }}
    >
      <div className='flex flex-row items-center justify-between p-3'>
        <div className='flex flex-row items-center gap-3'>
          <img
            src={`${IMAGE_CDN_URL}/upi-tile.png`}
            alt='upi_logo'
            className='h-8 w-8 object-contain rounded rounded-md bg-white p-0.5'
          />
          <div className='text-sm font-medium gap-1 flex flex-col'>
            {mode.title}
            {!hideOfferBadge && (
              <div
                className={classNames(
                  'flex items-center gap-1 px-1.5 py-0.5 rounded-md w-fit ',
                  !isLogoContainer ? 'mt-1' : ''
                )}
                style={{ backgroundColor: parsedColors.offerBadge.backgroundColor }}
              >
                <Icon
                  IconComponent={SealPercent}
                  size='xxs'
                  iconProps={{ style: { color: parsedColors.offerBadge.textColor } }}
                />
                <p className='text-text-xxs-medium' style={{ color: parsedColors.offerBadge.textColor }}>
                  {paymentMethodsConfig?.find(pm => pm.paymentMethod === mode.mode)?.discountText || 'Extra 5% off'}
                </p>
              </div>
            )}
          </div>
        </div>
        <div className='flex items-center gap-1'>
          <div className='text-sm font-medium'>{1599.55}</div>
          {/* Adding an empty container just to match the placement of the price with the rest of the cards */}
          <div className='h-3.5 w-3.5'>{''}</div>
        </div>
      </div>
      {isMobile ? (
        <MobileUpiPreview
          selectedApps={
            getActiveApps(mode.modeConfig?.selected_apps || []).length
              ? mode.modeConfig?.selected_apps ?? fallbackUpiApps
              : fallbackUpiApps
          }
          textColor={parsedColors.paymentCard.textColor}
          otherApps={mode.modeConfig?.other_apps ?? []}
        />
      ) : (
        <DesktopUpiPreview
          selectedApps={
            getActiveApps(mode.modeConfig?.selected_apps || []).length
              ? mode.modeConfig?.selected_apps ?? fallbackUpiApps
              : fallbackUpiApps
          }
          borderColor={parsedColors.paymentCard.borderColor}
          textColor={parsedColors.paymentCard.textColor}
        />
      )}
    </div>
  )
}

const StandardTileContent = ({
  mode,
  parsedColors,
  isLogoContainer,
  paymentMethodsConfig,
  hideOfferBadge
}: {
  mode: PaymentModeUiConfig
  parsedColors: ParsedColors
  isLogoContainer: boolean
  paymentMethodsConfig?: UIPreviewPaymentMethodType[]
  hideOfferBadge?: boolean
}) => (
  <div>
    <div className='flex flex-col '>
      <div className='flex gap-1.5 items-center w-fit'>
        <h3 className='text-text-sm-medium w-fit truncate' style={{ color: parsedColors.paymentCard.textColor }}>
          {mode.title}
        </h3>
        {isLogoContainer && <LogoComponent logos={mode.icons} size='sm' />}
      </div>
      {!mode.paymentCard.isSubtitleHidden && (
        <p className='text-text-xs-regular ' style={{ color: parsedColors.paymentCard.subtitleColor }}>
          {mode.subtitle}
        </p>
      )}
      {mode.mode !== 'COD' && !hideOfferBadge && (
        <div
          className={classNames(
            'flex items-center gap-1 px-1.5 py-0.5 rounded-md w-fit ',
            !isLogoContainer ? 'mt-1' : ''
          )}
          style={{ backgroundColor: parsedColors.offerBadge.backgroundColor }}
        >
          <Icon
            IconComponent={SealPercent}
            size='xxs'
            iconProps={{ style: { color: parsedColors.offerBadge.textColor } }}
          />
          <p className='text-text-xxs-medium' style={{ color: parsedColors.offerBadge.textColor }}>
            {paymentMethodsConfig?.find(pm => pm.paymentMethod === mode.mode)?.discountText || 'Extra 5% off'}
          </p>
        </div>
      )}
    </div>

    <div className='ml-auto items-center flex'>
      <p className='text-text-sm-medium' style={{ color: parsedColors.paymentCard.textColor }}>
        1599
      </p>
      <span className='text-text-xxs-medium' style={{ color: parsedColors.paymentCard.textColor }}>
        .55
      </span>
      <Icon
        IconComponent={CaretRight}
        size='xxs'
        iconProps={{ style: { color: parsedColors.paymentCard.textColor } }}
      />
    </div>
  </div>
)

export const MethodContainer = ({
  mode,
  isSelected,
  isHovered,
  onSelect,
  hasAnySelected,
  bannerSelected,
  paymentMethodsConfig,
  hideOfferBadge
}: {
  mode: PaymentModeUiConfig
  isSelected: boolean
  isHovered: boolean
  onSelect: () => void
  hasAnySelected: boolean
  bannerSelected: boolean
  paymentMethodsConfig?: UIPreviewPaymentMethodType[]
  hideOfferBadge?: boolean
}) => {
  const {
    getters: { paymentTileConfigs, device }
  } = usePaymentPage()
  const isLogoContainer = paymentTileConfigs.layout === 'LOGOS'
  const layoutUi = paymentTileConfigs.layoutUi

  const parsedColors: ParsedColors = {
    paymentCard: {
      borderColor: mode.paymentCard.borderColor || layoutUi.paymentCard.borderColor,
      backgroundColor: mode.paymentCard.backgroundColor || layoutUi.paymentCard.backgroundColor,
      textColor: mode.paymentCard.textColor || layoutUi.paymentCard.textColor,
      iconColor: mode.paymentCard.iconColor || layoutUi.paymentCard.iconColor,
      subtitleColor: mode.paymentCard.subtitleColor || layoutUi.paymentCard.subtitleColor
    },
    offerBadge: {
      backgroundColor: mode.offerBadge.backgroundColor || layoutUi.offerBadge.backgroundColor,
      textColor: mode.offerBadge.textColor || layoutUi.offerBadge.textColor
    }
  }

  const isMobile = device === DeviceType.MOBILE

  const isUpiIntentModeSelected = mode.mode === 'UPI_INTENT'

  const card = (
    <div
      className={classNames(
        `flex items-center ${
          !isUpiIntentModeSelected && 'px-3 py-2.5'
        } border-[1.5px] rounded-2xl gap-3 cursor-pointer hover:border-coal-dark hover:shadow-[0_0_0_100px_rgba(113,113,123,0.15)_inset] min-h-14`,
        isHovered ? 'z-10 ring-2 ring-black/50 shadow-lg ' : 'border-transparent',
        hasAnySelected && !isSelected ? 'opacity-50' : '',
        bannerSelected ? 'opacity-50' : ''
      )}
      onClick={e => {
        e.stopPropagation()
        onSelect()
      }}
      style={{
        borderColor: isHovered ? undefined : parsedColors.paymentCard.borderColor,
        backgroundColor: parsedColors.paymentCard.backgroundColor
      }}
    >
      {!isLogoContainer && !isUpiIntentModeSelected && (
        <PaymentMethodIcon paymentMethod={mode.mode} iconColor={parsedColors.paymentCard.iconColor} />
      )}
      {isUpiIntentModeSelected ? (
        <UpiIntentTileContent
          mode={mode}
          parsedColors={parsedColors}
          isMobile={isMobile}
          isLogoContainer={isLogoContainer}
          paymentMethodsConfig={paymentMethodsConfig}
          hideOfferBadge={hideOfferBadge}
        />
      ) : (
        <StandardTileContent
          mode={mode}
          parsedColors={parsedColors}
          isLogoContainer={isLogoContainer}
          paymentMethodsConfig={paymentMethodsConfig}
          hideOfferBadge={hideOfferBadge}
        />
      )}
    </div>
  )

  if (mode.isSuggested) {
    return (
      <SuggestedMethodWrapper
        title={mode.suggestedTile.title}
        backgroundColor={mode.suggestedTile.backgroundColor}
        textColor={mode.suggestedTile.textColor}
        isDimmed={hasAnySelected && !isSelected}
      >
        {card}
      </SuggestedMethodWrapper>
    )
  }

  return card
}

export default PaymentMethodsPreview
